import fontManifest from "../../../config/map-fonts.json" with { type: "json" };

export const MAP_FONT_GROUPS = fontManifest.groups || [];
export const MAP_FONT_REGISTRY = fontManifest.fonts;
export const MAP_FONTS = MAP_FONT_REGISTRY.filter((font) => font.selectable !== false);

const VALID_ACCESS = new Set(["free", "professional"]);
if (!Array.isArray(MAP_FONT_REGISTRY) || MAP_FONT_REGISTRY.some((font) => !font?.id || !font?.family || !VALID_ACCESS.has(font.access))) {
  throw new Error("Invalid map-fonts.json: every font must declare access as free or professional");
}

export function createMapFontResolver(manifest, { warn = () => {} } = {}) {
  const fonts = Array.isArray(manifest?.fonts) ? manifest.fonts.filter((font) => font?.id && font?.family) : [];
  if (!fonts.length) throw new Error("A map font registry requires at least one valid font");
  const byId = new Map(fonts.map((font) => [font.id, font]));
  const aliases = new Map();
  for (const font of fonts) {
    for (const alias of [font.family, ...(font.aliases || [])]) {
      if (typeof alias === "string" && alias.trim()) aliases.set(alias.trim().toLowerCase(), font.id);
    }
  }
  const configuredDefault = byId.get(manifest.defaultFont);
  const defaultFont = configuredDefault || fonts[0];
  if (!configuredDefault) warn(`Configured default map font "${manifest.defaultFont}" is unavailable; using "${defaultFont.id}".`);
  return {
    defaultFont,
    resolve(value) {
      if (typeof value === "string") {
        const requested = value.trim();
        if (byId.has(requested)) return byId.get(requested);
        const aliasId = aliases.get(requested.toLowerCase());
        if (aliasId) return byId.get(aliasId);
      }
      return defaultFont;
    },
  };
}

const fontResolver = createMapFontResolver(fontManifest, { warn: (message) => console.warn(message) });
export const DEFAULT_MAP_FONT = fontResolver.defaultFont.id;
const loadPromises = new Map();

export function resolveMapFont(value) {
  return fontResolver.resolve(value);
}

export function normalizeMapFont(value) {
  return resolveMapFont(value)?.id || null;
}

export function getMapFont(value) {
  return resolveMapFont(value);
}

export function getSelectableMapFonts({ canSelectProfessional = false, currentFontId } = {}) {
  return MAP_FONT_REGISTRY.filter((font) => (
    (font.selectable !== false || font.id === currentFontId)
    && (font.access === "free" || canSelectProfessional || font.id === currentFontId)
  ));
}

export function getMapFontCssVariables(value) {
  const font = getMapFont(value);
  return { "--map-font-family": font ? `"${font.family}", Arial, sans-serif` : "Arial, sans-serif" };
}

export function mapFontFileName(value) {
  return `${normalizeMapFont(value)}-latin-wght-normal.woff2`;
}

export const MAP_EXPORT_WEIGHTS = [400, 500, 600, 700, 800];

export function normalizeMapFontWeight(weight) {
  const requested = Number.parseInt(weight, 10) || 400;
  return MAP_EXPORT_WEIGHTS.reduce((closest, candidate) => (
    Math.abs(candidate - requested) < Math.abs(closest - requested) ? candidate : closest
  ), MAP_EXPORT_WEIGHTS[0]);
}

export function resolveMapFontExportWeight(value, weight) {
  const font = getMapFont(value);
  const requested = normalizeMapFontWeight(weight);
  const mapped = font?.exportWeightMap?.[String(requested)] ?? requested;
  return Number.parseInt(mapped, 10) || 400;
}

export function mapFontExportFileName(value, weight = 400) {
  const id = normalizeMapFont(value);
  const resolvedWeight = resolveMapFontExportWeight(id, weight);
  return `${id}/${id}-${resolvedWeight}.ttf`;
}

export async function ensureMapFontLoaded(value, fontBaseUrl = "/fonts", { loadDefault = false } = {}) {
  const font = getMapFont(value);
  if ((font.id === DEFAULT_MAP_FONT && !loadDefault) || typeof FontFace === "undefined") return font;
  const base = String(fontBaseUrl || "/fonts").replace(/\/+$/, "");
  const key = `${font.id}:${base}`;
  if (!loadPromises.has(key)) {
    loadPromises.set(key, (async () => {
      const face = new FontFace(
        font.family,
        `url("${base}/${mapFontFileName(font.id)}") format("woff2")`,
        { style: "normal", weight: font.fontWeight || "100 900", display: "swap" }
      );
      const loaded = await face.load();
      document.fonts.add(loaded);
      return font;
    })().catch((error) => {
      loadPromises.delete(key);
      throw error;
    }));
  }
  return loadPromises.get(key);
}

export async function getMapFontEmbedCss(value, fontBaseUrl = "/fonts") {
  const font = getMapFont(value);
  const base = String(fontBaseUrl || "/fonts").replace(/\/+$/, "");
  const response = await fetch(`${base}/${mapFontFileName(font.id)}`);
  if (!response.ok) throw new Error(`Unable to load ${font.name} for export`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return `@font-face{font-family:"${font.family}";src:url(data:font/woff2;base64,${btoa(binary)}) format("woff2");font-style:normal;font-weight:${font.fontWeight || "100 900"};font-display:swap;}`;
}
